import PropTypes from 'prop-types';
import { memo, useCallback, useState } from 'react';

import PopperTooltip from 'shared/ui/Popper/PopperTooltip';
import { stringifyError } from 'shared/functions';
import { BaseAttachmentDto } from '@eva/types/dto';

import SpinnerSmall from '../Spinner/SpinnerSmall';

import { isResume } from './functions';

export interface FileButtonParseProps<T> {
  file: T;
  onSubmit: (file: T) => Promise<T>;
  formValues: {
    parse: boolean;
    files: File[];
  };
}

const FileButtonParse = <F extends BaseAttachmentDto>(props: FileButtonParseProps<F>) => {
  const { file, onSubmit, formValues } = props;
  const [parsing, setParsing] = useState(false);
  const [parsingError, setParsingError] = useState(null);

  const handleParse = useCallback(() => {
    setParsing(true);
    setParsingError('');

    return onSubmit(file)
      .then(() => {
        setParsing(false);
        return Promise.resolve(file);
      })
      .catch((err) => {
        setParsing(false);
        setParsingError(stringifyError(err));
        return Promise.reject(err);
      });
  }, [onSubmit, file]);

  if (!isResume(file) || (isResume(file) && file.parsed) || formValues.parse) {
    return null;
  }

  const hasError = file.hasError || parsingError;

  return (
    <div className="relative">
      {hasError && (
        <PopperTooltip placement="right" overlay={translate('Parsing error')}>
          <span className="badge bg-danger table-cv-badge">
            <i className="fa fa-exclamation-circle" />
          </span>
        </PopperTooltip>
      )}

      <PopperTooltip placement="top" overlay={translate('Parse CV')}>
        <button
          type="button"
          className="btn btn-xs btn-box-tool no-padding"
          style={{ fontSize: '14px' }}
          disabled={hasError}
          onClick={handleParse}
        >
          {parsing ? <SpinnerSmall /> : <i className="fa fa-file-code-o" />}
        </button>
      </PopperTooltip>
    </div>
  );
};

FileButtonParse.contextTypes = {
  isAllowedOperation: PropTypes.func.isRequired,
};

FileButtonParse.propTypes = {
  file: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

// eslint-disable-next-line import/no-default-export
export default memo(FileButtonParse);
